import { useState, useEffect, useRef } from "react";
import { apiRequest } from "@/api/base";
import type { SiteVersion } from "@/types/sites";
import {
  assembleSiteVersion,
  fetchManifestByHost,
  type ViewerManifest,
} from "@/utils/viewerManifest";
import { applyTradeAccentCssVars } from "@/utils/paletteUtils";

// Cache TTL: 5 minutes in dev, 10 minutes in production
const TTL_MS = (import.meta.env.PROD ? 10 : 5) * 60 * 1000;

interface PublishedVersionResponse extends SiteVersion {
  subdomain?: string;
  siteId?: string;
}

export const usePublishedVersionBySubdomain = (
  subdomain: string | null,
  language?: string
) => {
  const [publishedVersion, setPublishedVersion] =
    useState<PublishedVersionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const versionRef = useRef<{ id?: string; updatedAt?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPublishedVersion = async () => {
      if (!subdomain) {
        setError("No subdomain detected");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const urlParams = new URLSearchParams(window.location.search);
        const isDraft = urlParams.get("draft") === "true";
        const backendToken = urlParams.get("backend_token");
        const version = urlParams.get("version");
        const urlLangParam = urlParams.get("language");
        const requestedLang = urlLangParam || language;

        if (isDraft && backendToken) {
          try {
            const params: Record<string, string> = {
              subdomain,
              draft: "true",
              backend_token: backendToken,
            };
            if (version) params.version = version;
            if (requestedLang) params.language = requestedLang;

            const draftVersion = await apiRequest<PublishedVersionResponse>(
              `/api/website-content/published`,
              "GET",
              undefined,
              false,
              { params }
            );

            if (draftVersion?.config?.theme?.paletteName) {
              try {
                const { loadPalette } = await import("@/utils/palettes");
                const palette = await loadPalette(
                  draftVersion.config.theme.paletteName
                );
                if (palette) {
                  draftVersion.config.theme.classes = {
                    ...draftVersion.config.theme.classes,
                    ...palette.classes,
                  };
                  // Sync Trade accent CSS variables with palette accent color
                  

                  applyTradeAccentCssVars(draftVersion.config.theme);
                }
              } catch (paletteError) {
                console.warn(
                  `Failed to load palette "${draftVersion.config.theme.paletteName}":`,
                  paletteError
                );
              }
            }

            if (!cancelled) {
              setPublishedVersion({ ...draftVersion, subdomain });
              versionRef.current = {
                id: draftVersion.versionId,
                updatedAt: String(
                  draftVersion.updatedAt ?? new Date().toISOString()
                ),
              };
              setIsLoading(false);
            }
            return;
          } catch (draftError) {
            console.error("Failed to fetch draft version via API:", draftError);
            if (!cancelled) {
              setError(
                draftError instanceof Error && draftError.message
                  ? draftError.message
                  : "Failed to load draft preview"
              );
              setIsLoading(false);
            }
            return;
          }
        }

        const host = window.location.hostname;
        const isLocalhost = host.endsWith(".localhost");
        const useHostManifest = !isLocalhost;
        const cacheKey = useHostManifest
          ? `viewerManifest:host:${host}`
          : `viewerManifest:subdomain:${subdomain}`;
        let hadCache = false;

        try {
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const raw = JSON.parse(cached) as any;
            const wrapper =
              raw &&
              typeof raw === "object" &&
              "data" in raw &&
              "cachedAt" in raw
                ? raw
                : { data: raw, cachedAt: 0 };

            const manifest = wrapper.data as ViewerManifest | null;
            const cachedAt =
              typeof wrapper.cachedAt === "number" ? wrapper.cachedAt : 0;
            const isFresh = cachedAt > 0 && Date.now() - cachedAt < TTL_MS;

            if (manifest) {
              if (manifest.suspension?.active) {
                try {
                  localStorage.setItem(
                    cacheKey,
                    JSON.stringify({ data: manifest, cachedAt: Date.now() })
                  );
                } catch {
                  /* ignore cache write errors */
                }
                if (!cancelled) {
                  setPublishedVersion(null);
                  setError(
                    manifest.suspension.message ||
                      "This site is currently suspended"
                  );
                  setIsLoading(false);
                }
              } else {
                const { version: cachedVersion } = assembleSiteVersion(
                  manifest,
                  requestedLang
                );
                if (!cancelled) {
                  setPublishedVersion({
                    ...(cachedVersion as any),
                    siteId: manifest.siteId,
                    subdomain,
                  });
                  versionRef.current = {
                    id: manifest.versionId,
                    updatedAt: manifest.publishedAt,
                  };
                  setIsLoading(false);
                }
              }
              hadCache = true;
              if (isFresh) {
                return;
              }
            }
          }
        } catch {
          /* ignore cache errors */
        }

        try {
          let manifest: ViewerManifest | null = null;

          // Use manifest injected by Cloudflare Worker if available (avoids network fetch)
          const injected = (window as any).__GENLP_MANIFEST__ as ViewerManifest | undefined;
          if (injected) {
            manifest = injected;
            delete (window as any).__GENLP_MANIFEST__;
          } else if (useHostManifest) {
            manifest = await fetchManifestByHost(host);
          } else {
            const devHost = `${subdomain}.genlp.ai`;
            try {
              console.debug("[viewer] fetching manifest for dev host", devHost);
              manifest = await fetchManifestByHost(devHost);
            } catch {
              manifest = null;
            }
            if (!manifest) {
              console.debug(
                "[viewer] fetching manifest for subdomain fallback",
                subdomain
              );
              manifest = await fetchManifestByHost(subdomain);
            }
          }

          if (!manifest) {
            throw new Error("manifest unavailable");
          }

          if (manifest.suspension?.active) {
            try {
              localStorage.setItem(
                cacheKey,
                JSON.stringify({ data: manifest, cachedAt: Date.now() })
              );
            } catch {
              /* ignore cache write errors */
            }
            if (!cancelled) {
              setPublishedVersion(null);
              setError(
                manifest.suspension.message ||
                  "This site is currently suspended"
              );
              setIsLoading(false);
            }
            return;
          }

          const { version: manifestVersion } = assembleSiteVersion(
            manifest,
            requestedLang
          );

          if (manifestVersion?.config?.theme?.paletteName) {
            try {
              const { loadPalette } = await import("@/utils/palettes");
              const palette = await loadPalette(
                manifestVersion.config.theme.paletteName
              );
              if (palette) {
                manifestVersion.config = {
                  ...manifestVersion.config,
                  theme: {
                    ...manifestVersion.config.theme,
                    classes: {
                      ...manifestVersion.config.theme?.classes,
                      ...palette.classes,
                    },
                  },
                } as typeof manifestVersion.config;
                // Sync Trade accent CSS variables with palette accent color
                

                applyTradeAccentCssVars(manifestVersion.config.theme);
              }
            } catch (paletteError) {
              console.warn(
                `Failed to load palette "${manifestVersion.config?.theme?.paletteName}":`,
                paletteError
              );
            }
          }

          if (!cancelled) {
            setPublishedVersion({
              ...(manifestVersion as any),
              siteId: manifest.siteId,
              subdomain,
            });
            versionRef.current = {
              id: manifest.versionId,
              updatedAt: manifest.publishedAt,
            };
            setIsLoading(false);
          }

          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify({ data: manifest, cachedAt: Date.now() })
            );
          } catch {
            /* ignore cache write errors */
          }
          return;
        } catch (manifestError) {
          console.error(
            "Failed to fetch viewer manifest for subdomain:",
            manifestError
          );

          if (!hadCache) {
            try {
              const params: Record<string, string> = { subdomain };
              if (requestedLang) params.language = requestedLang;

              const fallbackVersion =
                await apiRequest<PublishedVersionResponse>(
                  `/api/website-content/published`,
                  "GET",
                  undefined,
                  false,
                  { params }
                );

              if (fallbackVersion?.config?.theme?.paletteName) {
                try {
                  const { loadPalette } = await import("@/utils/palettes");
                  const palette = await loadPalette(
                    fallbackVersion.config.theme.paletteName
                  );
                  if (palette) {
                    fallbackVersion.config.theme.classes = {
                      ...fallbackVersion.config.theme.classes,
                      ...palette.classes,
                    };
                    // Sync Trade accent CSS variables with palette accent color
                    

                    applyTradeAccentCssVars(fallbackVersion.config.theme);
                  }
                } catch (paletteError) {
                  console.warn(
                    `Failed to load palette "${fallbackVersion.config.theme.paletteName}":`,
                    paletteError
                  );
                }
              }

              if (!cancelled) {
                setPublishedVersion({ ...fallbackVersion, subdomain });
                versionRef.current = {
                  id: fallbackVersion.versionId,
                  updatedAt: String(
                    fallbackVersion.updatedAt ?? new Date().toISOString()
                  ),
                };
              }
            } catch (fallbackError) {
              console.error(
                "Fallback API fetch for subdomain failed:",
                fallbackError
              );
              if (!cancelled) {
                const message =
                  fallbackError instanceof Error && fallbackError.message
                    ? fallbackError.message
                    : "Failed to load site";
                if (message.toLowerCase().includes("suspend")) {
                  setPublishedVersion(null);
                }
                setError(message);
              }
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error && err.message
              ? err.message
              : "Failed to load site";
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchPublishedVersion();

    return () => {
      cancelled = true;
    };
  }, [subdomain, language]);

  const siteId = publishedVersion?.siteId ?? null;

  const memoized = useRef<PublishedVersionResponse | null>(null);
  if (publishedVersion) {
    memoized.current = publishedVersion;
  }

  return {
    publishedVersion: memoized.current,
    siteId,
    isLoading,
    error,
  };
};
